/**
 * OAuth for a page with no server.
 *
 * Authorization Code with PKCE, run entirely in the browser. There is no client
 * secret anywhere — a secret shipped to a browser is not a secret, and every
 * provider here supports public clients precisely so it need not exist.
 *
 * The verifier is generated per attempt, kept in sessionStorage (not local), and
 * deleted the moment it is exchanged. State is checked on return, so a redirect
 * we did not start is rejected rather than processed.
 */

const VERIFIER_KEY = 'agl.oauth.verifier';
const STATE_KEY = 'agl.oauth.state';
const PENDING_KEY = 'agl.oauth.pending';

function randomUrlSafe(bytes = 64): string {
  const raw = new Uint8Array(bytes);
  crypto.getRandomValues(raw);
  return base64url(raw.buffer);
}

function base64url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function challengeFor(verifier: string): Promise<string> {
  return base64url(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier)));
}

export interface OAuthApp {
  clientId: string;
  authorizeUrl: string;
  tokenUrl: string;
  scopes: string[];
  /** Extra params some providers need on the authorize call. */
  extraAuthParams?: Record<string, string>;
}

export interface TokenSet {
  accessToken: string;
  refreshToken?: string;
  /** Epoch ms. Absent means the provider did not say. */
  expiresAt?: number;
}

export function redirectUri(): string {
  // Must match what is registered with the provider exactly, so no trailing
  // slash and no query string.
  return `${window.location.origin}/auth/callback`;
}

/** Send the browser off to the provider. Returns only if something is wrong. */
export async function beginAuth(providerId: string, app: OAuthApp): Promise<void> {
  if (!app.clientId) {
    throw new Error(
      'This provider has no client id configured yet. See docs/ONE_CLICK_STORAGE.md.'
    );
  }
  const verifier = randomUrlSafe();
  const state = randomUrlSafe(16);

  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(PENDING_KEY, providerId);

  const params = new URLSearchParams({
    client_id: app.clientId,
    response_type: 'code',
    redirect_uri: redirectUri(),
    state,
    code_challenge: await challengeFor(verifier),
    code_challenge_method: 'S256',
    ...(app.scopes.length ? { scope: app.scopes.join(' ') } : {}),
    ...(app.extraAuthParams ?? {}),
  });

  window.location.assign(`${app.authorizeUrl}?${params.toString()}`);
}

export interface CallbackResult {
  providerId: string;
  tokens: TokenSet;
}

/**
 * Handle the redirect back. Returns null when this is not an OAuth return.
 *
 * Throws on a state mismatch rather than proceeding: a code arriving with the
 * wrong state is either a stale tab or someone else's redirect, and exchanging
 * it would attach the wrong account.
 */
export async function completeAuth(
  lookup: (providerId: string) => OAuthApp | undefined
): Promise<CallbackResult | null> {
  const url = new URL(window.location.href);
  const code = url.searchParams.get('code');
  const returnedState = url.searchParams.get('state');
  const error = url.searchParams.get('error');

  if (!code && !error) return null;

  const providerId = sessionStorage.getItem(PENDING_KEY);
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  const expectedState = sessionStorage.getItem(STATE_KEY);
  clearPending();

  // Strip the code out of the address bar either way, so a reload cannot
  // replay it and the credential does not sit in history.
  window.history.replaceState({}, '', window.location.origin + window.location.pathname);

  if (error) {
    throw new Error(
      url.searchParams.get('error_description') ?? `The provider refused: ${error}`
    );
  }
  if (!providerId || !verifier) {
    throw new Error('That sign-in did not start here. Try connecting again.');
  }
  if (!expectedState || returnedState !== expectedState) {
    throw new Error('Sign-in could not be verified. Try connecting again.');
  }

  const app = lookup(providerId);
  if (!app) throw new Error('Unknown provider.');

  const tokens = await exchange(app, { grant_type: 'authorization_code', code: code!, code_verifier: verifier });
  return { providerId, tokens };
}

export function clearPending(): void {
  sessionStorage.removeItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(PENDING_KEY);
}

async function exchange(app: OAuthApp, extra: Record<string, string>): Promise<TokenSet> {
  const body = new URLSearchParams({
    client_id: app.clientId,
    redirect_uri: redirectUri(),
    ...extra,
  });

  const res = await fetch(app.tokenUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });

  const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    const detail = (json.error_description ?? json.error ?? `HTTP ${res.status}`) as string;
    throw new Error(`Could not complete sign-in: ${detail}`);
  }

  const expiresIn = Number(json.expires_in);
  return {
    accessToken: String(json.access_token),
    refreshToken: json.refresh_token ? String(json.refresh_token) : undefined,
    expiresAt: Number.isFinite(expiresIn) ? Date.now() + expiresIn * 1000 : undefined,
  };
}

/** Refresh 60s early, so a request never leaves with a token about to lapse. */
const REFRESH_MARGIN_MS = 60_000;

export function isExpired(tokens: TokenSet): boolean {
  return !!tokens.expiresAt && Date.now() > tokens.expiresAt - REFRESH_MARGIN_MS;
}

/**
 * Swap a refresh token for a fresh access token.
 *
 * Providers may or may not return a new refresh token; when they do not, the
 * old one stays valid and must be carried forward, or the next refresh fails.
 */
export async function refresh(app: OAuthApp, tokens: TokenSet): Promise<TokenSet> {
  if (!tokens.refreshToken) {
    throw new Error('This connection has expired and cannot renew itself. Connect again.');
  }
  const next = await exchange(app, {
    grant_type: 'refresh_token',
    refresh_token: tokens.refreshToken,
  });
  return { ...next, refreshToken: next.refreshToken ?? tokens.refreshToken };
}
